import { useState, useEffect, useMemo } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Area, AreaChart, CartesianGrid, XAxis, Line, LabelList } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/shadcn/chart";
import { Checkbox } from "@/components/shadcn/checkbox";
import dayjs from "dayjs";
import { dateFormat, getPointsBadge } from "@/lib/utils";
import { Loader } from "../../common/loader";

const chartConfig = {
    playerA: {
        label: "Player A",
        color: "var(--color-chart-vivid-blue)",
    },
    playerB: {
        label: "Player B",
        color: "#ff9500",
    },
} satisfies ChartConfig;

const kpiOptions = [
    { value: "totalPoints", label: "Total Points", color: "blue" },
    { value: "offensivePoints", label: "Offensive", color: "red" },
    { value: "defensivePoints", label: "Defensive", color: "green" },
    { value: "teamPlayPoints", label: "Team Play", color: "yellow" },
    { value: "effortPoints", label: "Effort", color: "purple" },
    { value: "bonusPoints", label: "Bonus", color: "orange" },
];

const goalkeeperKpiOptions = [
    { value: "totalPoints", label: "Total Points", color: "blue" },
    { value: "gsaxPoints", label: "GSAx", color: "cyan" },
    { value: "highDangerPoints", label: "High Danger", color: "red" },
    { value: "lowDangerPoints", label: "Low Danger", color: "green" },
    { value: "lateralPassPoints", label: "Lateral Pass", color: "yellow" },
    { value: "screenedPoints", label: "Screened", color: "purple" },
];

const calculateTrendLine = (data: MatchData[], key: string): MatchData[] => {
    const filteredData = data.filter((d) => {
        const value = d[key];
        return value !== undefined && value !== null && typeof value === "number";
    });

    if (filteredData.length < 2) return data;

    const n = filteredData.length;
    let sumX = 0,
        sumY = 0,
        sumXY = 0,
        sumXX = 0;

    filteredData.forEach((d, i) => {
        const x = i;
        const y = d[key] as number;
        sumX += x;
        sumY += y;
        sumXY += x * y;
        sumXX += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return data.map((d, i) => ({
        ...d,
        [`${key}_trend`]: slope * i + intercept,
    }));
};

const CustomLabel = ({
    x,
    y,
    value,
    playerId,
}: {
    x?: string | number;
    y?: string | number;
    value?: string | number | null | boolean;
    playerId?: "A" | "B";
}) => {
    if ((!value && value !== 0) || !x || !y) return null;

    const xCoord = typeof x === "string" ? parseFloat(x) : x;
    const yCoord = typeof y === "string" ? parseFloat(y) : y;
    const numValue = typeof value === "string" ? parseFloat(value) : value;

    if (typeof numValue !== "number" || isNaN(xCoord) || isNaN(yCoord) || isNaN(numValue)) return null;

    const playerColor = playerId === "A" ? "var(--color-chart-vivid-blue)" : "#ff9500";
    const displayValue = numValue % 1 === 0 ? numValue.toString() : numValue;

    return (
        <g transform={`translate(${xCoord}, ${yCoord})`}>
            <text
                x={0}
                y={playerId === "A" ? -15 : -25}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={playerColor}
                fontSize={12}
                fontWeight="bold"
                style={{ userSelect: "none" }}
            >
                {displayValue}
            </text>
        </g>
    );
};

interface KpiData {
    matchDate: string;
    homeTeam: string;
    awayTeam: string;
    homeGoals: number;
    awayGoals: number;
    totalPoints: number;
    offensiveData?: { points: number };
    defensiveData?: { points: number };
    teamPlayData?: { points: number };
    effortData?: { points: number };
    bonusData?: { points: number };
    gsaxData?: { points: number };
    highDangerData?: { points: number };
    lowDangerData?: { points: number };
    lateralPassData?: { points: number };
    screenedData?: { points: number };
    role: string;
}

interface TransformedMatch {
    date: string;
    matchDate: string;
    opponent: string;
    result: "W" | "D" | "L";
    score: string;
    totalPoints: number;
    offensivePoints?: number;
    defensivePoints?: number;
    teamPlayPoints?: number;
    effortPoints?: number;
    bonusPoints?: number;
    gsaxPoints?: number;
    highDangerPoints?: number;
    lowDangerPoints?: number;
    lateralPassPoints?: number;
    screenedPoints?: number;
    role: string;
}

interface PlayerData {
    kpis?: KpiData[];
}

interface MatchData {
    date: string;
    matchNumber: number;
    matchDate: string;
    playerA?: number;
    playerB?: number;
    playerAOpponent?: string;
    playerBOpponent?: string;
    playerAResult?: "W" | "D" | "L";
    playerBResult?: "W" | "D" | "L";
    playerAScore?: string;
    playerBScore?: string;
    playerARole?: string;
    playerBRole?: string;
    commonMatch?: boolean;
    [key: string]: string | number | boolean | undefined;
}

interface ComparisonMatchPerformanceChartProps {
    playerAData?: PlayerData;
    playerBData?: PlayerData;
    playerAName: string;
    playerBName: string;
    playerATeamName?: string;
    playerBTeamName?: string;
    selectedKPI?: string;
    onKPIChange?: (kpi: string) => void;
    isLoading?: boolean;
    viewMode?: "average" | "total";
}

export function ComparisonMatchPerformanceChart({
    playerAData,
    playerBData,
    playerAName,
    playerBName,
    playerATeamName,
    playerBTeamName,
    selectedKPI: propSelectedKPI,
    onKPIChange,
    isLoading,
}: ComparisonMatchPerformanceChartProps) {
    const [selectedKPI, setSelectedKPI] = useState(propSelectedKPI || "totalPoints");
    const [showCommonMatchesOnly, setShowCommonMatchesOnly] = useState(false);
    const [showTrendLine, setShowTrendLine] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (propSelectedKPI && propSelectedKPI !== selectedKPI) {
            setSelectedKPI(propSelectedKPI);
        }
    }, [propSelectedKPI, selectedKPI]);

    const handleKPIChange = (kpi: string) => {
        setSelectedKPI(kpi);
        onKPIChange?.(kpi);
    };

    const isGoalkeeperComparison = playerAData?.kpis?.[0]?.role === "GK" || playerBData?.kpis?.[0]?.role === "GK";
    const currentKpiOptions = isGoalkeeperComparison ? goalkeeperKpiOptions : kpiOptions;
    const currentKPILabel = currentKpiOptions.find((option) => option.value === selectedKPI)?.label || "Total Points";

    const chartData = useMemo(() => {
        if (!playerAData?.kpis && !playerBData?.kpis) return [];

        const transformKpis = (kpis: KpiData[], teamName: string = "Vålerenga"): TransformedMatch[] =>
            kpis
                .sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime())
                .map((kpi) => {
                    const isHome = kpi.homeTeam === teamName;
                    const homeGoals = kpi.homeGoals;
                    const awayGoals = kpi.awayGoals;

                    const baseMatch = {
                        date: dayjs(kpi.matchDate).format(dateFormat),
                        matchDate: kpi.matchDate,
                        opponent: isHome ? kpi.awayTeam : kpi.homeTeam,
                        result: isHome
                            ? ((homeGoals > awayGoals ? "W" : homeGoals === awayGoals ? "D" : "L") as "W" | "D" | "L")
                            : ((awayGoals > homeGoals ? "W" : awayGoals === homeGoals ? "D" : "L") as "W" | "D" | "L"),
                        score: isHome ? `${homeGoals}-${awayGoals}` : `${awayGoals}-${homeGoals}`,
                        totalPoints: kpi.totalPoints,
                        role: kpi.role,
                    };

                    if (kpi.role === "GK") {
                        return {
                            ...baseMatch,
                            gsaxPoints: kpi.gsaxData?.points,
                            highDangerPoints: kpi.highDangerData?.points,
                            lowDangerPoints: kpi.lowDangerData?.points,
                            lateralPassPoints: kpi.lateralPassData?.points,
                            screenedPoints: kpi.screenedData?.points,
                        };
                    } else {
                        return {
                            ...baseMatch,
                            offensivePoints: kpi.offensiveData?.points,
                            defensivePoints: kpi.defensiveData?.points,
                            teamPlayPoints: kpi.teamPlayData?.points,
                            effortPoints: kpi.effortData?.points,
                            bonusPoints: kpi.bonusData?.points,
                        };
                    }
                });

        const playerAMatches = playerAData?.kpis ? transformKpis(playerAData.kpis, playerATeamName ?? "Vålerenga") : [];
        const playerBMatches = playerBData?.kpis ? transformKpis(playerBData.kpis, playerBTeamName ?? "Vålerenga") : [];

        const allMatchDates = new Set([
            ...playerAMatches.map((m) => m.matchDate),
            ...playerBMatches.map((m) => m.matchDate),
        ]);

        const sortedMatchDates = Array.from(allMatchDates).sort(
            (a, b) => new Date(a).getTime() - new Date(b).getTime()
        );

        let combinedData: MatchData[] = sortedMatchDates.map((matchDate, index) => {
            const playerAMatch = playerAMatches.find((m) => m.matchDate === matchDate);
            const playerBMatch = playerBMatches.find((m) => m.matchDate === matchDate);

            const getPlayerValue = (match: TransformedMatch | undefined, key: string): number | undefined => {
                if (!match) return undefined;
                const value = match[key as keyof TransformedMatch];
                return typeof value === "number" ? value : undefined;
            };

            return {
                date: dayjs(matchDate).format(dateFormat),
                matchNumber: index + 1,
                matchDate,
                playerA: getPlayerValue(playerAMatch, selectedKPI),
                playerB: getPlayerValue(playerBMatch, selectedKPI),
                playerAOpponent: playerAMatch?.opponent,
                playerBOpponent: playerBMatch?.opponent,
                playerAResult: playerAMatch?.result,
                playerBResult: playerBMatch?.result,
                playerAScore: playerAMatch?.score,
                playerBScore: playerBMatch?.score,
                playerARole: playerAMatch?.role,
                playerBRole: playerBMatch?.role,
                commonMatch: !!(playerAMatch && playerBMatch),
            };
        });

        if (showCommonMatchesOnly) {
            combinedData = combinedData.filter((match) => match.commonMatch);
        }

        if (showTrendLine && combinedData.length > 1) {
            combinedData = calculateTrendLine(combinedData, "playerA");
            combinedData = calculateTrendLine(combinedData, "playerB");
        }

        return combinedData;
    }, [playerAData, playerBData, selectedKPI, showCommonMatchesOnly, showTrendLine, isGoalkeeperComparison]);

    if (isLoading) {
        return (
            <div className="glass card card-slide-up rounded-xl p-6 flex items-center justify-center h-[300px] sm:h-[518px]">
                <Loader message="Loading match performance chart..." className="text-sm" />
            </div>
        );
    }

    if (!playerAData && !playerBData && !isMobile) {
        return (
            <div className="glass card card-slide-up rounded-xl p-6 flex items-center justify-center h-[300px] sm:h-[518px]">
                <p className="text-on-surface-variant">Choose players to view match performance comparison</p>
            </div>
        );
    }

    return (
        <div className="glass card card-slide-up rounded-xl p-3 sm:p-6 flex flex-col h-full animate-delay-700">
            <div className="flex flex-col gap-2 mb-2 shrink-0 sm:flex-row sm:items-center sm:justify-between sm:mb-0.5">
                <h3 className="text-sm sm:text-lg font-headline font-bold text-on-surface">
                    Match Performance Comparison •{" "}
                    <span
                        style={{
                            color: `var(--color-general-${kpiOptions.find((option) => option.value === selectedKPI)?.color || "blue"})`,
                        }}
                    >
                        {currentKPILabel}
                    </span>
                </h3>
                <nav className="rounded-md p-1 bg-surface-container-low w-full sm:w-auto shrink-0">
                    <div className="grid grid-cols-3 gap-1 sm:flex sm:items-center sm:gap-0">
                        {currentKpiOptions.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => handleKPIChange(option.value)}
                                className={`nav-tab text-center ${selectedKPI === option.value ? "nav-tab-active" : "nav-tab-inactive"}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </nav>
            </div>

            <div className="flex flex-wrap items-center justify-center mb-2 mt-1.5 gap-3 sm:gap-4">
                <label className="flex items-center gap-2 text-sm text-on-surface cursor-pointer select-none">
                    <span>Show trend lines</span>
                    <Checkbox
                        checked={showTrendLine}
                        onCheckedChange={(checked) => setShowTrendLine(checked === true)}
                        className="cursor-pointer border-gray-400 bg-transparent data-[state=checked]:bg-transparent data-[state=checked]:border-gray-400 data-[state=checked]:text-gray-700 [&>*>svg]:stroke-[4]"
                    />
                </label>
                <label className="flex items-center gap-2 text-sm text-on-surface cursor-pointer select-none">
                    <span>Common matches only</span>
                    <Checkbox
                        checked={showCommonMatchesOnly}
                        onCheckedChange={(checked) => setShowCommonMatchesOnly(checked === true)}
                        className="cursor-pointer border-gray-400 bg-transparent data-[state=checked]:bg-transparent data-[state=checked]:border-gray-400 data-[state=checked]:text-gray-700 [&>*>svg]:stroke-[4]"
                    />
                </label>
            </div>

            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
                <ChartContainer
                    config={chartConfig}
                    className="h-[240px] sm:h-[400px] w-full"
                    style={{
                        minWidth: isMobile ? `${Math.max(300, chartData.length * 28)}px` : "100%",
                    }}
                >
                    <AreaChart
                        data={chartData}
                        margin={
                            isMobile
                                ? { top: 35, right: 8, left: 8, bottom: 5 }
                                : { top: 50, right: 30, left: 30, bottom: 20 }
                        }
                    >
                        <defs>
                            <linearGradient id="fillPlayerA" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="var(--color-chart-vivid-blue)" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="var(--color-chart-vivid-blue)" stopOpacity={0.1} />
                            </linearGradient>
                            <linearGradient id="fillPlayerB" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ff9500" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#ff9500" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>

                        <CartesianGrid vertical={false} stroke="rgba(148, 163, 184, 0.1)" strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tick={
                                isMobile || chartData.length > 20
                                    ? (props: { x?: number; y?: number; index?: number }) => (
                                          <text
                                              x={props.x}
                                              y={(props.y ?? 0) + 12}
                                              fill="var(--color-gray-400)"
                                              fontSize={isMobile ? 8 : 10}
                                              textAnchor="middle"
                                          >
                                              {(props.index ?? 0) + 1}
                                          </text>
                                      )
                                    : { fill: "var(--color-gray-400)", fontSize: 10 }
                            }
                            type="category"
                            interval={0}
                            padding={{ left: 20, right: 20 }}
                        />

                        <ChartTooltip
                            cursor={{
                                stroke: "rgba(59, 130, 246, 0.3)",
                                strokeWidth: 2,
                                strokeDasharray: "5 5",
                                strokeOpacity: 0.6,
                            }}
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;

                                const dataPoint = chartData.find((item) => item.date === label);
                                if (!dataPoint) return null;

                                const opponent = dataPoint.playerAOpponent ?? dataPoint.playerBOpponent;
                                const result = dataPoint.playerAResult ?? dataPoint.playerBResult;
                                const score = dataPoint.playerAScore ?? dataPoint.playerBScore;
                                const showBadge = selectedKPI === "totalPoints";
                                const grayBadge = {
                                    bg: "bg-gray-100",
                                    text: "text-gray-500",
                                    border: "border-gray-200",
                                };
                                const playerABadge =
                                    showBadge && dataPoint.playerA !== undefined
                                        ? dataPoint.playerA === 0
                                            ? grayBadge
                                            : getPointsBadge(dataPoint.playerA)
                                        : null;
                                const playerBBadge =
                                    showBadge && dataPoint.playerB !== undefined
                                        ? dataPoint.playerB === 0
                                            ? grayBadge
                                            : getPointsBadge(dataPoint.playerB)
                                        : null;

                                return (
                                    <div className="chart-tooltip min-w-[220px]">
                                        <div className="flex flex-col gap-2">
                                            <div className="font-semibold text-sm text-general-blue">
                                                Match {dataPoint.matchNumber} - {dataPoint.date}
                                            </div>
                                            {opponent && (
                                                <div className="text-xs text-on-surface flex items-center gap-2 flex-wrap">
                                                    <span>vs {opponent}</span>
                                                    {result && score && (
                                                        <span
                                                            className={`px-2 py-1 rounded text-xs font-bold ${
                                                                result === "W"
                                                                    ? "bg-win-background text-win"
                                                                    : result === "D"
                                                                      ? "bg-draw-background text-draw"
                                                                      : "bg-loss-background text-loss"
                                                            }`}
                                                        >
                                                            {result} {score}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {dataPoint.playerA !== undefined && (
                                                <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                    <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                                                    <span className="text-sm font-medium text-on-surface">
                                                        {playerAName}:
                                                    </span>
                                                    {playerABadge ? (
                                                        <div
                                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${playerABadge.bg} ${playerABadge.text} rounded font-bold text-sm border ${playerABadge.border}`}
                                                        >
                                                            {dataPoint.playerA}
                                                        </div>
                                                    ) : (
                                                        <span className="text-lg font-bold text-blue-600">
                                                            {dataPoint.playerA}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {dataPoint.playerB !== undefined && (
                                                <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                    <div
                                                        className="w-3 h-3 rounded-full"
                                                        style={{ backgroundColor: "#ff9500" }}
                                                    ></div>
                                                    <span className="text-sm font-medium text-on-surface">
                                                        {playerBName}:
                                                    </span>
                                                    {playerBBadge ? (
                                                        <div
                                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${playerBBadge.bg} ${playerBBadge.text} rounded font-bold text-sm border ${playerBBadge.border}`}
                                                        >
                                                            {dataPoint.playerB}
                                                        </div>
                                                    ) : (
                                                        <span
                                                            className="text-lg font-bold"
                                                            style={{ color: "#ff9500" }}
                                                        >
                                                            {dataPoint.playerB}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <Area
                            dataKey="playerA"
                            type="linear"
                            fill="url(#fillPlayerA)"
                            stroke="var(--color-chart-vivid-blue)"
                            strokeWidth={3}
                            dot={{
                                r: 4,
                                fill: "var(--color-chart-vivid-blue)",
                                strokeWidth: 0,
                            }}
                            activeDot={{
                                r: 6,
                                fill: "var(--color-chart-vivid-blue)",
                                strokeWidth: 2,
                                stroke: "white",
                            }}
                            connectNulls={false}
                        >
                            <LabelList
                                dataKey="playerA"
                                position="top"
                                content={({ value, x, y }) => <CustomLabel value={value} x={x} y={y} playerId="A" />}
                            />
                        </Area>
                        <Area
                            dataKey="playerB"
                            type="linear"
                            fill="url(#fillPlayerB)"
                            stroke="#FF9500"
                            strokeWidth={3}
                            dot={{
                                r: 4,
                                fill: "#FF9500",
                                strokeWidth: 0,
                            }}
                            activeDot={{
                                r: 6,
                                fill: "#FF9500",
                                strokeWidth: 2,
                                stroke: "white",
                            }}
                            connectNulls={false}
                        >
                            <LabelList
                                dataKey="playerB"
                                position="top"
                                content={({ value, x, y }) => <CustomLabel value={value} x={x} y={y} playerId="B" />}
                            />
                        </Area>
                        {showTrendLine && (
                            <>
                                {playerAData && (
                                    <Line
                                        dataKey="playerA_trend"
                                        type="linear"
                                        stroke="#6A95F1"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        activeDot={false}
                                    />
                                )}
                                {playerBData && (
                                    <Line
                                        dataKey="playerB_trend"
                                        type="linear"
                                        stroke="#FFBE5C"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        activeDot={false}
                                    />
                                )}
                            </>
                        )}
                    </AreaChart>
                </ChartContainer>
            </div>
        </div>
    );
}
